import type { StyleConfig } from '../StyleConfig';
import bindings from './bindings.json';

export interface MapPresetMeta {
  name: string;
  label: string;
  data: StyleConfig;
}

const presetModules = import.meta.glob<StyleConfig>('./*.json', {
  eager: true,
  import: 'default',
});

function nameFromPath(path: string): string {
  // './hero.json' -> 'hero'
  return path.replace(/^\.\//, '').replace(/\.json$/, '');
}

function labelFromName(name: string): string {
  return name
    .split('-')
    .map((part) => (part.length === 0 ? part : part[0].toUpperCase() + part.slice(1)))
    .join(' ');
}

function buildRegistry(): MapPresetMeta[] {
  const out: MapPresetMeta[] = [];
  for (const [path, data] of Object.entries(presetModules)) {
    const name = nameFromPath(path);
    if (name === 'bindings') continue;
    out.push({ name, label: labelFromName(name), data });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

export const MAP_PRESETS: MapPresetMeta[] = buildRegistry();

export const TRACK_PRESET_BINDINGS: Record<string, string> = bindings as Record<string, string>;

export const PRESETS_DIR = 'src/render/presets' as const;
export const BINDINGS_FILE = 'src/render/presets/bindings.json' as const;

export function presetForName(name: string): MapPresetMeta | null {
  return MAP_PRESETS.find((p) => p.name === name) ?? null;
}

export function presetForTrack(trackUrl: string): MapPresetMeta | null {
  const name = TRACK_PRESET_BINDINGS[trackUrl];
  if (!name) return null;
  return presetForName(name);
}

export function presetFilePath(name: string): `src/render/presets/${string}.json` {
  return `src/render/presets/${name}.json`;
}

export function isValidPresetName(name: string): boolean {
  return /^[a-z0-9][a-z0-9-]{0,40}$/.test(name) && name !== 'bindings';
}
